import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react"

// Default config values
const DEFAULT_CONFIG = {
  companyName: "FlexFlow",
}

// Storage key
const STORAGE_KEY = "flexflow-app-config"

// App Config context
const AppConfigContext = createContext({
  config: DEFAULT_CONFIG,
  updateConfig: () => {},
  getCompanyName: () => DEFAULT_CONFIG.companyName,
})

// Hook to use app config context
export const useAppConfig = () => {
  const context = useContext(AppConfigContext)
  if (!context) {
    throw new Error("useAppConfig must be used within AppConfigProvider")
  }
  return context
}

// Load config from localStorage
const loadConfig = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) {
      return { ...DEFAULT_CONFIG, ...JSON.parse(saved) }
    }
  } catch (e) {
    console.error("Error loading app config:", e)
  }
  return DEFAULT_CONFIG
}

// Standalone function to get company name (can be used outside React components)
export const getCompanyName = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) {
      const config = JSON.parse(saved)
      return config.companyName || DEFAULT_CONFIG.companyName
    }
  } catch (e) {
    console.error("Error getting company name:", e)
  }
  return DEFAULT_CONFIG.companyName
}

// Save config to localStorage
const saveConfig = (config) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config))
  } catch (e) {
    console.error("Error saving app config:", e)
  }
}

// App Config provider
export const AppConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(loadConfig)

  // Save config to localStorage when changed
  useEffect(() => {
    saveConfig(config)
  }, [config])

  // Update config with partial values
  const updateConfig = useCallback((updates) => {
    setConfig((prev) => ({ ...prev, ...updates }))
  }, [])

  // Get company name helper
  const getCompanyName = useCallback(() => {
    return config.companyName || DEFAULT_CONFIG.companyName
  }, [config.companyName])

  const value = useMemo(
    () => ({
      config,
      updateConfig,
      getCompanyName,
    }),
    [config, updateConfig, getCompanyName]
  )

  return <AppConfigContext.Provider value={value}>{children}</AppConfigContext.Provider>
}

export default AppConfigContext
